/**
 * Reservation lifecycle + read HTTP request schemas — SCRUM-277 (DS-020 §16 slice 7).
 *
 * Wire source of truth (CLAUDE.md rule 3 — validators are the single source) for
 * the booking lifecycle endpoints (create / ingest / modify / cancel) and the
 * read path (GET list/calendar). The controller (apps/api booking interface,
 * Pass B) reads each via its DTO `static schema`, so the global validation pipe
 * rejects a malformed body before the handler runs.
 *
 * Shapes mirror the ALREADY-SHIPPED application inputs:
 *   - `guestBindingSchema` ⇔ `GuestBindingInput`
 *     (apps/api/.../reservation/reservation-lifecycle.shared.ts).
 *   - `createReservationSchema` mirrors the preview stay fields + the create-only
 *     `instantBook` flag + the optional `guest` binding.
 *   - `ingestReservationSchema` ⇔ the body half of `IngestChannelReservationInput`
 *     (apps/api/.../reservation/ingest-channel-reservation.use-case.ts) — the
 *     `organizationId` is resolved from the route slug, never the body.
 *   - `modifyReservationSchema` ⇔ the body half of `ModifyReservationInput`.
 *   - `cancelReservationSchema` ⇔ the body half of the cancel input.
 *   - `listReservationsQuerySchema` is the GET list/calendar filter + pagination.
 *
 * Branded date fields carry the `LocalDateSchema` brand EXACTLY as the preview
 * schema does (SCRUM-223 H-001) — a real calendar date, not merely a string.
 *
 * `.strict()`: an unknown key is a malformed body → rejected, never silently
 * ignored. The guest binding is a UNION of two `.strict()` shapes so a body that
 * carries BOTH `guestId` and `displayName` is rejected (no single permissive
 * object would catch that).
 *
 * Cross-field invariants (checkOut > checkIn, nights ≤ MAX_STAY_NIGHTS, half-open
 * window span ≤ the capacity cap) are deliberately NOT here — they belong to the
 * domain / read boundary, which surfaces them as typed errors with their own HTTP
 * status. Encoding them here too would give one error class two conflicting codes.
 */
import { z } from 'zod';
import { asReportedLedgerSchema } from './as-reported-ledger.js';
import { channelReferenceNotesSchema } from './channel-reference-notes.schema.js';
import { LocalDateSchema } from '../time/index.js';
/**
 * Optional free-text discriminator — trimmed non-empty when present; absent or
 * `null` normalises to `null` (mirrors the preview schema's `optionalDiscriminator`,
 * since the application input is `string | null`, never `undefined`).
 */
const optionalDiscriminator = z.string().trim().min(1).nullable().default(null);
/**
 * `netRemittanceCents` (channel ingest) is a branded `Cents` integer at the wire
 * boundary: a non-negative safe integer (NOT a float — money is never dollars
 * here). Branded so the DTO mapper passes a `Cents` straight to the use case
 * without re-deriving it (SCRUM-223 H-001 branded-field discipline).
 */
const centsWireSchema = z
    .number()
    .int()
    .nonnegative()
    .max(Number.MAX_SAFE_INTEGER)
    .brand();
// ---------------------------------------------------------------------------
// SCRUM-279 — heardAbout provenance wire enum
// ---------------------------------------------------------------------------
/**
 * Wire enum for the HeardAbout provenance axis (§2.9). Must be set-equal to the
 * domain `HEARD_ABOUT_VALUES` catalog in
 * `apps/api/src/modules/booking/domain/reservation/value-objects/heard-about.vo.ts`.
 * The I3 drift-gate test enforces equality at build time (step 3b).
 */
export const HEARD_ABOUT_WIRE_VALUES = [
    'Social',
    'Email',
    'OTA',
    'Direct',
    'GEO',
    'Search',
];
/** Wire schema for the heardAbout provenance axis (closed z.enum of HEARD_ABOUT_WIRE_VALUES). */
export const heardAboutSchema = z.enum(HEARD_ABOUT_WIRE_VALUES);
/**
 * The channel-deliverable INPUT status set — values a client or channel MAY
 * supply when ingesting a reservation. `'overbooked'` is deliberately absent:
 * that state is only reachable internally via the overbooking-quarantine path
 * (SCRUM-362). Validated by `reservationIngestStatusWireSchema` (used in
 * `ingestReservationSchema` below).
 */
export const RESERVATION_INGEST_STATUS_WIRE_VALUES = [
    'QUO',
    'HOLD',
    'BKD',
    'CI',
    'CO',
    'CXL',
    'HIST',
];
/** Wire schema for the channel-deliverable INPUT status set (NO overbooked). */
export const reservationIngestStatusWireSchema = z.enum(RESERVATION_INGEST_STATUS_WIRE_VALUES);
/**
 * The readable/OUTPUT status set — the full set of statuses that may appear
 * on a serialized reservation (including `'overbooked'`, which is only reachable
 * internally via the overbooking-quarantine path). Used to serialize a
 * reservation's status on GET responses and ingest responses (SCRUM-362).
 */
export const RESERVATION_STATUS_WIRE_VALUES = [
    ...RESERVATION_INGEST_STATUS_WIRE_VALUES,
    'overbooked',
];
const reservationStatusWireSchema = z.enum(RESERVATION_STATUS_WIRE_VALUES);
// ---------------------------------------------------------------------------
// guestBindingSchema — { guestId } XOR { displayName, email?, phone? }
// ---------------------------------------------------------------------------
/** Existing-guest binding (the resolve path). `.strict()` so a smuggled
 *  `displayName`/`email` on this arm is rejected, not silently dropped. */
const existingGuestBindingSchema = z
    .object({
    guestId: z.string().uuid(),
})
    .strict();
/** New-guest binding (the create path). `displayName` is required + non-empty;
 *  `email`/`phone` are optional and may be explicitly `null`. `.strict()` so a
 *  smuggled `guestId` lands in neither arm and the union rejects it. */
const newGuestBindingSchema = z
    .object({
    displayName: z.string().min(1),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
})
    .strict();
/**
 * The §2.12 primary guest binding — a UNION of the two `.strict()` shapes. A body
 * with BOTH `guestId` and `displayName` matches neither strict arm and is
 * REJECTED (the XOR invariant). Absent guest (a guest-less reservation) is
 * expressed by omitting the field, not by an empty object.
 */
export const guestBindingSchema = z.union([existingGuestBindingSchema, newGuestBindingSchema]);
// ---------------------------------------------------------------------------
// createReservationSchema — stay fields + instantBook? + guest?
// ---------------------------------------------------------------------------
export const createReservationSchema = z
    .object({
    propertyId: z.string().uuid(),
    checkInDate: LocalDateSchema,
    checkOutDate: LocalDateSchema, // exclusive (morning of departure)
    guestCount: z.number().int().min(1),
    petCount: z.number().int().min(0).default(0),
    vehicleCount: z.number().int().min(0).default(0),
    /**
     * SCRUM-337 B1 occupancy seam — subset of `guestCount` that are children
     * (int >= 0, defaults to 0). Tracked/stored only; pricing is invariant to
     * the adults/children split. The cross-field adults >= 1 rule is enforced
     * by the domain Party at create, NOT here (a single field owns one code).
     */
    childCount: z.number().int().min(0).default(0),
    reservationType: optionalDiscriminator,
    bookingChannel: optionalDiscriminator,
    instantBook: z.boolean().optional(),
    guest: guestBindingSchema.optional(),
    /** SCRUM-279 — provenance axis (§2.9). Optional on the wire; the aggregate enforces
     *  requiredness for Direct/User stays (D1/D3). */
    heardAbout: heardAboutSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// ingestReservationSchema — channel-ingest body (org resolved from route slug)
// ---------------------------------------------------------------------------
export const ingestReservationSchema = z
    .object({
    propertyId: z.string().uuid(),
    reservationTypeKey: z.string().min(1),
    processor: z.string().min(1),
    /** The channel's stable reservation id (dedup key). `null` → keyless delivery. */
    channelReservationId: z.string().min(1).nullable(),
    arrivalDate: LocalDateSchema,
    departureDate: LocalDateSchema, // exclusive
    status: reservationIngestStatusWireSchema,
    guestCount: z.number().int().min(1).optional(),
    /** CALLER-SUPPLIED net remittance for ChannelCollect (§2.14 — NOT re-priced). */
    netRemittanceCents: centsWireSchema.optional(),
    channelId: z.string().uuid().optional(),
    rawPayload: z.record(z.unknown()).optional(),
    guest: guestBindingSchema.optional(),
    /** SCRUM-279 — provenance axis (§2.9). Optional on the wire; D5: do NOT auto-derive
     *  'OTA' on channel ingest — null is honest "not captured". */
    heardAbout: heardAboutSchema.optional(),
    // --- SCRUM-367 (DS-026 S5, F4) — mirrors the new optional fields on
    //     `IngestChannelReservationInput` (the "⇔" contract, §15). All optional
    //     ⇒ existing manual/QA callers are unaffected; the automated channel path
    //     is the primary producer (direct port call, not this HTTP body).
    /** §9.4/D10 — Phase-1 USD fence: the only accepted literal (a non-USD value is rejected). */
    currency: z.literal('USD').optional(),
    /** §9.2/D4 — channel-derived MoR override; `'needs-review'` = ambiguous signals. */
    collectionModel: z.enum(['ChannelCollect', 'PropertyCollect', 'needs-review']).optional(),
    /** §9.1 H3/D9 — the monotonic ordering key + tie-breaker (nullable — manual path omits). */
    sourceVersion: z.string().nullable().optional(),
    sourceVersionTiebreak: z.string().nullable().optional(),
    /** §9.3/D1 — the as-reported foreign ledger (reuses the single-source `asReportedLedgerSchema`). */
    asReported: asReportedLedgerSchema.optional(),
    /**
     * SCRUM-382 — operator-supplied Channel Reference Notes (informational). The
     * MANUAL counterpart to `asReported` for a non-integrated channel booking:
     * hand-entered gross / commission / channel-tax + a note, provenance
     * server-stamped `'operator-supplied'` (never `'channel'`). Strictly
     * informational — decoupled from the ledger/tax/owner-accounting; never
     * re-prices, never touches the $0-balance ChannelCollect invariant.
     */
    channelReferenceNotes: channelReferenceNotesSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// modifyReservationSchema — new stay dates (PATCH /:id)
// ---------------------------------------------------------------------------
export const modifyReservationSchema = z
    .object({
    checkInDate: LocalDateSchema,
    checkOutDate: LocalDateSchema, // exclusive
})
    .strict();
// ---------------------------------------------------------------------------
// cancelReservationSchema — cancellation pathway (nullable)
// ---------------------------------------------------------------------------
export const cancelReservationSchema = z
    .object({
    pathway: z.string().trim().min(1).nullable().default(null),
})
    .strict();
// ---------------------------------------------------------------------------
// listReservationsQuerySchema — read filter + pagination + half-open window
// ---------------------------------------------------------------------------
/**
 * A status query param may arrive as a single string (`?status=BKD`) or a
 * repeated/array param (`?status=BKD&status=CI`). Both normalise to a validated
 * `ReservationStatusValue[]`. The HALF-OPEN date window `[from, to)` is for the
 * calendar; the span cap (≤ MAX_STAY_NIGHTS days) is enforced at the read
 * boundary (the Prisma repo / query handler), NOT here — it is a resource guard,
 * not a per-field structural rule.
 */
export const listReservationsQuerySchema = z
    .object({
    propertyId: z.string().uuid().optional(),
    status: z
        .union([reservationStatusWireSchema, z.array(reservationStatusWireSchema)])
        .transform((s) => (Array.isArray(s) ? s : [s]))
        .optional(),
    from: LocalDateSchema.optional(),
    to: LocalDateSchema.optional(),
    // `z.coerce.number()` (not bare `z.number()`): GET query params arrive as
    // strings off the Fastify wire (`?limit=50` → `'50'`), so the read DTO must
    // coerce before the `.int()` / range guards. A genuine number (the unit-test
    // path) still passes, and a non-integer like `5.5` / `"5.5"` is still rejected
    // by `.int()` — coercion is backward-compatible with the number-based tests.
    // Mirrors the list-companies query DTO precedent (z.coerce.number for paging).
    limit: z.coerce.number().int().min(1).max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
})
    .strict();
//# sourceMappingURL=reservation-lifecycle.schema.js.map